import { BlockDiagram } from '@cadstrom/cadstrom-shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';

import { BlockDiagramViewer } from '#components/block-diagram/block-diagram-viewer.js';
import { useTRPC } from '#src/trpc.js';
import { ModelRenderFrame } from './model-render-frame.js';

import type { DesignModels } from '@cadstrom/sigma-engine-shared';

export interface AttachmentModelRenderProps {
	/** Id of the block-diagram attachment to load and render. */
	attachmentId: string;
	/** Fallback title when the document has none. */
	label?: string;
	/** Design to select within the compiled model. */
	designId?: string;
	/** Initial zoom box, in diagram coordinates. */
	bbox?: DesignModels.ModelBoundingBox;
}

/**
 * Model render sourced from a saved block-diagram attachment: loads the
 * document, compiles it at view time, and renders it in a framed embed with a
 * link that opens the revision's results-page block-diagram tab when the
 * current route provides an organization context.
 *
 * @param props - Component props.
 */
export function AttachmentModelRender({ attachmentId, label, designId, bbox }: AttachmentModelRenderProps) {
	const trpc = useTRPC();
	const { orgName } = useParams({ strict: false });
	const { data, isLoading, isError } = useQuery(
		trpc.requirementsV2.attachments.get.queryOptions({ attachmentId }, { enabled: attachmentId !== '' }),
	);

	if (isLoading) {
		return <div className='my-2 h-80 w-full animate-pulse rounded-lg border border-gray-a4 bg-gray-a2' />;
	}

	if (isError || data === undefined) {
		return (
			<div className='my-2 rounded-lg border border-gray-a4 p-3 text-sm text-gray-11'>
				Block diagram {label !== undefined && label !== '' ? `“${label}” ` : ''}could not be loaded.
			</div>
		);
	}

	const title = data.document.title !== '' ? data.document.title : (label ?? 'Block diagram');
	const projectId = data.document.generation.projectId;

	return (
		<ModelRenderFrame
			title={title}
			action={
				orgName !== undefined && (
					<Link
						to='/orgs/$orgName/projects/$projectId/revisions/$revisionId/results'
						params={{ orgName, projectId, revisionId: data.attachment.buildId }}
						search={(prev) => ({ ...prev, leftTab: 'block-diagram' as const })}
						className='text-xs whitespace-nowrap text-blue-11 hover:underline'
					>
						Open full view
					</Link>
				)
			}
		>
			<BlockDiagramViewer
				revisionId={data.attachment.buildId}
				document={data.document}
				variant={BlockDiagram.BlockDiagramVariantSchema.enum.full}
				designId={designId}
				bbox={bbox}
				hideNavigationControls
				className='h-full w-full'
			/>
		</ModelRenderFrame>
	);
}
